import React from 'react';
import { PropTypes as PT } from 'prop-types';
import _compact from 'lodash/compact';
import classes from './Tooltip.module.scss';

const caratSize = 6;

/**
 * Tooltip component
 * This component will display the tooltip after .5 seconds in the specified location
 * @param {Boolean} fixPosition - render the tooltip relative to the browser window ('fixed') which
 *        is useful if the tooltip is rendered within an element where the overflow is hidden
 * @param {Object} label - pass in the FormattedMessage component to display (required)
 * @param {String} location - 'top', 'left', 'bottom', or 'right' (default is bottom)
 * @param {Boolean} overlapCarat - if true (default), the carat will overlap the container;
 *         if false, the carat tip will barely touch the edge of the container
 * @param {String} shift - 'left' - will shift the tooltip container (not the pointer) to the left
 * @param {Object} style - any inline style overrides
 */
class Tooltip extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      style: {}
    };
  }

  componentDidMount() {
    if (this.props.fixPosition && this.tooltip) {
      this.tooltip.addEventListener('transitionend', this.onEndTransition);
    }
  }

  componentWillUnmount() {
    if (this.tooltip) {
      this.tooltip.removeEventListener('transitionend', this.onEndTransition);
    }
  }

  onEndTransition = () => {
    // each endTransition indicates that the tooltip is shown (there is no event on hide)
    const parent = this.tooltip.parentNode.getBoundingClientRect();
    const tip = {
      height: this.tooltip.offsetHeight,
      width: this.tooltip.offsetWidth
    };
    const tipPosition = {
      bottom: 'auto',
      left: 'auto',
      position: 'fixed',
      right: 'auto',
      top: 'auto',
      transform: 'none'
    };
    const caratOffset = this.props.overlapCarat ? 0 : caratSize;
    const offset = Object.assign({}, { top: 0, left: 0, bottom: 0, right: 0 }, this.props.offset);

    switch (this.props.location) {
      case 'top':
        tipPosition.left = parent.left + offset.left + (parent.width - tip.width) / 2;
        tipPosition.top = parent.top + offset.top - tip.height - caratOffset;
        if (this.props.shift === 'left') {
          tipPosition.left = parent.left + (parent.width - tip.width);
        }
        break;
      case 'right':
        tipPosition.left = parent.right + offset.right + caratOffset;
        tipPosition.top = parent.top + offset.top + (parent.height - tip.height) / 2;
        break;
      case 'left':
        tipPosition.left = parent.left + offset.left - tip.width - caratOffset;
        tipPosition.top = parent.top + offset.top + (parent.height - tip.height) / 2;
        break;
      default:
        // bottom
        tipPosition.left = parent.left + offset.left + (parent.width - tip.width) / 2;
        tipPosition.top = parent.bottom + offset.bottom + caratOffset;
        if (this.props.shift === 'left') {
          tipPosition.left = parent.left + offset.left + (parent.width - tip.width);
        }
        if (this.props.shift === 'right') {
          tipPosition.left = 'initial';
          tipPosition.right = parent.right + offset.right + (parent.width - tip.width);
        }
    }

    this.setState({
      style: tipPosition
    });
  };

  render() {
    const cssClasses = _compact([
      classes.toolTip,
      this.props.className,
      this.props.location === 'top' && classes.top,
      this.props.location === 'right' && classes.right,
      this.props.location === 'left' && classes.left,
      !['top', 'right', 'left'].includes(this.props.location) && classes.bottom,
      this.props.shift === 'left' && classes.shiftLeft,
      this.props.shift === 'right' && classes.shiftRight,
      this.props.overlapCarat && classes.snug,
      this.props.visible && classes.visible,
      this.props.useAltStyle && classes.altStyle
    ]).join(' ');

    return (
      <div
        className={cssClasses}
        ref={(elt) => {
          this.tooltip = elt;
        }}
        style={Object.assign({}, this.props.style, this.state.style)}
      >
        {this.props.label}
      </div>
    );
  }
}

Tooltip.propTypes = {
  className: PT.string,
  fixPosition: PT.bool,
  label: PT.oneOfType([PT.string, PT.object]).isRequired,
  location: PT.string,
  offset: PT.object,
  overlapCarat: PT.bool,
  shift: PT.string,
  style: PT.object,
  useAltStyle: PT.bool, // white tooltips for notifications
  visible: PT.bool // force tooltip to show
};

Tooltip.defaultProps = {
  location: 'bottom',
  overlapCarat: true,
  shift: null,
  style: null,
  offset: {
    top: 0,
    left: 0,
    bottom: 0,
    right: 0
  }
};

export default Tooltip;
